// Slide-in sheet that shows the public community Library — Trending,
// Recent, and the user's own entries — with one-click "Run this".
import { useEffect, useState } from "react";
import { Library as LibIcon, GitFork, ArrowUp, Play, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Entry {
  id: string;
  title: string;
  summary: string;
  kind: string;
  type_tag: string | null;
  upvotes: number;
  fork_count: number;
  publisher_user_id: string;
  created_at: string;
}

export default function LibrarySheet({ onRun, hideTrigger = false }: { onRun: (prompt: string) => void; hideTrigger?: boolean }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"trending" | "recent" | "mine">("trending");
  const [items, setItems] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    let q = supabase.from("library_entries").select("id,title,summary,kind,type_tag,upvotes,fork_count,publisher_user_id,created_at").limit(40);
    if (tab === "trending") q = q.eq("visibility", "public").order("upvotes", { ascending: false }).order("created_at", { ascending: false });
    else if (tab === "recent") q = q.eq("visibility", "public").order("created_at", { ascending: false });
    else q = (user ? q.eq("publisher_user_id", user.id) : q.eq("id", "00000000-0000-0000-0000-000000000000")).order("created_at", { ascending: false });
    q.then(({ data }) => { setItems((data as Entry[]) ?? []); setLoading(false); });
  }, [open, tab, user]);

  // Allow other surfaces (e.g. the QuickModeBar under the search) to
  // pop the library open without rendering their own trigger.
  useEffect(() => {
    const fn = () => setOpen(true);
    window.addEventListener("open-library", fn);
    return () => window.removeEventListener("open-library", fn);
  }, []);

  const upvote = async (id: string) => {
    if (!user) return;
    await supabase.from("library_upvotes").insert({ entry_id: id, user_id: user.id }).then(() => {
      setItems((arr) => arr.map((e) => e.id === id ? { ...e, upvotes: e.upvotes + 1 } : e));
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <SheetTrigger asChild>
          <button
            type="button"
            aria-label="Library"
            title="Community Library"
            className="h-9 w-9 grid place-items-center rounded-full border border-border bg-card/40 hover:border-primary/50 hover:bg-card transition-colors"
          >
            <LibIcon className="w-4 h-4" />
          </button>
        </SheetTrigger>
      )}
      <SheetContent side="right" className="w-full sm:max-w-md p-0">
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="font-mono flex items-center gap-2">
            <LibIcon className="w-4 h-4 text-primary" /> Library
          </SheetTitle>
          <p className="text-xs text-muted-foreground">A living collection of what the community has built. Fork, upvote, or run.</p>
        </SheetHeader>
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className="w-full">
          <TabsList className="grid grid-cols-3 mx-5 mt-3">
            <TabsTrigger value="trending">Trending</TabsTrigger>
            <TabsTrigger value="recent">Recent</TabsTrigger>
            <TabsTrigger value="mine">Mine</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="px-5 pb-8 pt-4 mt-0 overflow-y-auto max-h-[calc(100vh-180px)]">
            {loading ? (
              <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
            ) : items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-10">Nothing here yet.</p>
            ) : (
              <ul className="space-y-3">
                {items.map((e) => (
                  <li key={e.id} className="rounded-xl border border-border bg-card/40 p-3 hover:border-primary/40 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-sm text-foreground truncate">{e.title || "Untitled"}</p>
                        {e.summary && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{e.summary}</p>}
                        <div className="flex items-center gap-3 mt-2 text-[10px] font-mono text-muted-foreground">
                          <span className="px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">{e.type_tag ?? e.kind}</span>
                          <span className="inline-flex items-center gap-1"><ArrowUp className="w-3 h-3" /> {e.upvotes}</span>
                          <span className="inline-flex items-center gap-1"><GitFork className="w-3 h-3" /> {e.fork_count}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => { onRun(e.title); setOpen(false); }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary text-primary-foreground text-[11px] font-mono font-semibold hover:opacity-90"
                        >
                          <Play className="w-3 h-3" /> Run
                        </button>
                        {user && (
                          <button
                            type="button"
                            onClick={() => upvote(e.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[11px] font-mono text-muted-foreground hover:text-foreground hover:border-primary/40"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
